// The Memory side panel (M7-04; workspace-modes note §11.5; design "What Recall knows about
// {subject}"): beside any mode, what Recall knows about the focused subject (a task, seat, team,
// person or DE), or about the company when nothing is focused. One call, `memory/search` with the
// subject as its scope; the subject's own name is the question unless the person types another.
// Only the memory scopes the viewer can read are searched (the API's rule). When Recall is not
// connected the panel says so instead of showing an empty list.
// CHAT-PANEL (2026-10-28): beside a chat conversation it is "Related memory": the same call and
// scope, asked with the conversation (chat-memory-context.ts) instead of the company's name.
import {
  Badge,
  Empty,
  EmptyDescription,
  EmptyHeader,
  EmptyTitle,
  InfoTip,
  Input,
  Skeleton,
} from "@companyos/ui";
import { useQuery } from "@tanstack/react-query";
import { Link, useRouteContext } from "@tanstack/react-router";
import { SearchIcon } from "lucide-react";
import { useEffect, useId, useState } from "react";
import type { PanelProps } from "../../extensions/registry";
import { keepFocus } from "../../focus/focus";
import { conversationLabel, useChatMemoryContext } from "../chat/chat-memory-context";
import { isManager } from "../company";
import { problemText } from "../de/de-status";
import { memorySearchQuery } from "./memory-queries";
import { RecallNotice } from "./memory-review-screen";

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

const SUBJECT_LABEL = {
  seat: "this seat",
  person: "this person",
  task: "this task",
  de: "this digital employee",
  team: "this team",
} as const;

/** The memory scope of the focused subject (`company` when there is none or it is not a uuid). */
export function scopeOf(subject: PanelProps["focus"]["subject"]): string {
  if (!subject || !UUID.test(subject.id)) return "company";
  return `${subject.kind}:${subject.id}`;
}

export function MemoryPanel({ focus, company, modeKey }: PanelProps) {
  const { api } = useRouteContext({ from: "__root__" });
  const scope = scopeOf(focus.subject);
  const [typed, setTyped] = useState("");
  const [q, setQ] = useState("");
  const inputId = useId();
  // CHAT-PANEL: beside a conversation, the question is the conversation itself (its channel,
  // topic, thread root and DE); the scope and its permission checks stay the subject's.
  const chat = useChatMemoryContext(company.id, focus, modeKey === "chat");
  const label = chat ? conversationLabel(chat.context) : "";
  const conversation = chat ? `${label}|${focus.thread ?? ""}` : "";
  // A new subject (or conversation) starts from its own context again.
  // biome-ignore lint/correctness/useExhaustiveDependencies: reset on the subject only
  useEffect(() => {
    setTyped("");
    setQ("");
  }, [scope, conversation]);
  const contextual = chat !== null && !q.trim() && chat.question !== "";
  const asked = q.trim() ? q : contextual ? chat.question : "";
  const search = useQuery({
    ...memorySearchQuery(api, company.id, scope, asked),
    enabled: chat === null || chat.ready,
  });
  const about = focus.subject ? SUBJECT_LABEL[focus.subject.kind] : company.name;
  return (
    // CHAT-PANEL: a flush tab: the context line and the search box stay put, the links stay at
    // the bottom, and only the list between them scrolls.
    <div className="flex min-h-0 min-w-0 flex-1 flex-col" data-panel="memory">
      <div className="flex shrink-0 flex-col gap-3 px-4 pt-4 pb-3">
        {chat ? (
          <p
            className="flex min-w-0 items-center gap-1 text-sm text-muted-foreground"
            data-slot="memory-context"
          >
            <span className="min-w-0 truncate">
              {q.trim()
                ? `About “${q.trim()}”`
                : focus.thread
                  ? `This thread${label ? ` · ${label}` : ""}`
                  : label || `About ${about}`}
            </span>
            <InfoTip label="About related memory">
              Recall is asked with this conversation: its channel and topic, the open thread's first
              message and the digital employee it goes to. Only memory you can see.
            </InfoTip>
          </p>
        ) : (
          <p className="text-sm text-muted-foreground">What Recall knows about {about}.</p>
        )}
        <form
          aria-label="Ask memory"
          className="relative"
          onSubmit={(e) => {
            e.preventDefault();
            setQ(typed);
          }}
        >
          <label htmlFor={inputId} className="sr-only">
            Ask memory
          </label>
          <SearchIcon
            aria-hidden="true"
            className="pointer-events-none absolute top-1/2 left-2.5 size-3.5 -translate-y-1/2 text-muted-foreground"
          />
          <Input
            id={inputId}
            className="pl-8"
            placeholder="Ask about something else"
            value={typed}
            onChange={(e) => setTyped(e.target.value)}
          />
        </form>
      </div>
      <div
        className="flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto px-4 pb-4"
        data-slot="memory-body"
      >
        {search.isPending ? (
          <div role="status" aria-label="Searching memory" className="flex flex-col gap-2">
            <Skeleton className="h-14 w-full" />
            <Skeleton className="h-14 w-full" />
          </div>
        ) : search.isError ? (
          <p role="alert" className="text-sm text-destructive">
            {problemText(search.error, "Memory could not be searched.")}
          </p>
        ) : (
          <>
            <RecallNotice recall={search.data.recall} />
            {search.data.recall.state === "connected" && !search.data.hits.length ? (
              <Empty className="flex-none border border-dashed border-border">
                <EmptyHeader>
                  <EmptyTitle>Nothing yet</EmptyTitle>
                  <EmptyDescription>
                    {contextual
                      ? "Nothing related to this conversation in the memory you can see."
                      : `Recall knows nothing about “${search.data.query}” in the scopes you can see.`}
                  </EmptyDescription>
                </EmptyHeader>
              </Empty>
            ) : null}
            {search.data.hits.length ? (
              <ul className="flex flex-col gap-2" data-slot="memory-hits">
                {search.data.hits.map((h, i) => (
                  <li
                    // biome-ignore lint/suspicious/noArrayIndexKey: hits have no id; order is rank
                    key={`${h.scope.id}:${h.type}:${h.title}:${i}`}
                    className="flex min-w-0 flex-col gap-1 rounded-md border border-border p-3"
                  >
                    <div className="flex min-w-0 flex-wrap items-center gap-1.5">
                      <Badge variant="accent">{h.type}</Badge>
                      <span className="min-w-0 text-sm font-medium break-words">{h.title}</span>
                    </div>
                    {h.preview ? (
                      <p className="line-clamp-3 text-xs text-muted-foreground">{h.preview}</p>
                    ) : null}
                    {h.why ? <p className="text-xs">Why: {h.why}</p> : null}
                    <p className="text-2xs text-muted-foreground">
                      {h.scope.name ?? h.scope.kind}
                      {/* RECALL-FOLLOWUP: an atom shows as its record, never as `a:<hash>`. */}
                      {h.atom ? " · matched a detail of this record" : ""}
                    </p>
                  </li>
                ))}
              </ul>
            ) : null}
          </>
        )}
      </div>
      <div className="flex shrink-0 flex-wrap gap-x-3 gap-y-1 border-t border-border px-4 py-2.5 text-xs">
        <Link
          to="/c/$slug/work/memory/review"
          params={{ slug: company.slug }}
          search={keepFocus}
          className="text-primary underline-offset-4 hover:underline"
        >
          Memory review
        </Link>
        {isManager(company) ? (
          <Link
            to="/c/$slug/setup/recall"
            params={{ slug: company.slug }}
            search={keepFocus}
            className="text-primary underline-offset-4 hover:underline"
          >
            Recall settings
          </Link>
        ) : null}
      </div>
    </div>
  );
}
